import classNames from "classnames";
import { LocalDate, LocalDateTime, LocalTime } from "@js-joda/core";
import React from "react";
import { useMap } from "react-map-gl/maplibre";
import { OnboardingVisitId, VisitBroadcastId } from "../messages";
import { STRINGS } from "../strings";
import { AppCaregiver } from "../system/AppState/AppCaregiverState";
import { Visit } from "../system/AppState/AppVisitsState";
import { translate } from "../translate";
import { List } from "../utils/List";
import { MAP_AVAILABLE_VISIT_SVG, MAP_CAREGIVER_SVG } from "../utils/svgs";
import { CaregiverPositionMapMarker } from "./AvailableVisitsLiveMap";
import MapFabs from "./AvailableVisitsMapFabs";
import VisitMarker from "./AvailableVisitsMapVisitMarker";
import { AppSlider } from "./Generic/AppSlider";

interface Props {
  caregiver: AppCaregiver;
  visits: List<Visit>;
  onVisitClick: (visitBroadcastId: VisitBroadcastId) => void;
  onOnboardingVisitClick: (onboardingVisitId: OnboardingVisitId) => void;
}

const AvailableVisitsMapForNonMedflytCaregivers = (props: Props) => {
  const map = useMap();
  const [state, setState] = React.useState({
    minDaysBefore: 0,
    maxDaysBefore: 30,
    isFiltersOpen: false,
  });

  const hanleClickHomeFab = () => {
    map.current?.flyTo({
      zoom: 14,
      animate: true,
      duration: 1,
      center: { ...props.caregiver.addressGeoLocation },
    });
  };

  const handleDurationChange = (value: [number, number]) => {
    setState({
      ...state,
      minDaysBefore: value[0],
      maxDaysBefore: value[1],
    });
  };

  const filterVisitsByDate = (visit: Visit) => {
    const minDate = LocalDateTime.of(
      LocalDate.now().plusDays(state.minDaysBefore),
      LocalTime.MIDNIGHT
    );
    const maxDate = LocalDateTime.of(LocalDate.now().plusDays(state.maxDaysBefore), LocalTime.MAX);

    return (
      visit.publicVisitBroadcastDetails.startTimeLocal.isAfter(minDate) &&
      visit.publicVisitBroadcastDetails.startTimeLocal.isBefore(maxDate)
    );
  };

  const disableScroll = () => {
    document.ontouchmove = (e) => {
      e.preventDefault();
    };
  };

  const enableScroll = () => {
    document.ontouchmove = () => {
      return true;
    };
  };

  return (
    <>
      <CaregiverPositionMapMarker position={props.caregiver.addressGeoLocation} />
      {props.visits.filter(filterVisitsByDate).map((visit) => {
        return (
          <VisitMarker
            key={`marker-${visit.type}-${visit.id}`}
            visit={visit}
            position={visit.publicVisitBroadcastDetails.location.location}
            isCaregiverOfMedflyt={props.caregiver.isCaregiverOfMedFlyt}
            popupProps={{
              origin: props.caregiver.addressGeoLocation,
              visitIconHeight: 44.25,
              visitIconWidth: 37.5,
              goToVisit: props.onVisitClick,
              goToOnboardingVisit: props.onOnboardingVisitClick,
              goToPendingConfirmationVisit: () =>
                console.error("Cannot open pending confirmation visit from other agency"),
            }}
          />
        );
      })}

      <MapFabs
        filtersFabOptions={{
          isFiltersSecionOpen: state.isFiltersOpen,
          onClick: () => setState({ ...state, isFiltersOpen: !state.isFiltersOpen }),
        }}
        homeFabOptions={{
          onClick: hanleClickHomeFab,
        }}
      />

      <FiltersContainer
        isFiltersOpen={state.isFiltersOpen}
        minDaysBefore={state.minDaysBefore}
        maxDaysBefore={state.maxDaysBefore}
        disableScroll={disableScroll}
        enableScroll={enableScroll}
        onChangeDuration={handleDurationChange}
      />
    </>
  );
};

export default AvailableVisitsMapForNonMedflytCaregivers;

const FiltersContainer = (props: {
  isFiltersOpen: boolean;
  minDaysBefore: number;
  maxDaysBefore: number;
  disableScroll: () => void;
  enableScroll: () => void;
  onChangeDuration: (value: [number, number]) => void;
}) => {
  const renderLegend = (icon: JSX.Element, baseClass: string, label: string) => {
    return (
      <div className={`AvailableVisitsLiveMap__legendItem ${baseClass}-${label}`}>
        {icon}
        <span>{label}</span>
      </div>
    );
  };

  return (
    <div
      className={classNames({
        AvailableVisitsLiveMap__bottomContainer: true,
        Active: props.isFiltersOpen,
      })}
    >
      <div className="AvailableVisitsLiveMap__bottomContainerHeader">
        {translate(STRINGS.SHOW_ME_CASES_IN_THE_NEXT)}
      </div>

      <div className="AvailableVisitsLiveMap__AppSliderContainer">
        <AppSlider
          onBeforeChange={props.disableScroll}
          onAfterChange={props.enableScroll}
          withBars={true}
          barClassName={"AvailabilityScreen__bar"}
          className="AvailableVisitsLiveMap__slider"
          max={30}
          min={0}
          minDistance={3}
          value={[props.minDaysBefore, props.maxDaysBefore]}
          step={1}
          snapDragDisabled={true}
          onChange={(value) => {
            if (!Array.isArray(value) || value.length < 2) {
              throw new Error("The Impossible Happened");
            }
            props.onChangeDuration([value[0], value[1]]);
          }}
          startHandler={{
            direction: "start",
            children: (
              <div className="AvailableVisitsLiveMap__daysRange">
                <span>{props.minDaysBefore}</span>
                <span>{translate(STRINGS.DAYS)}</span>
              </div>
            ),
          }}
          endHandler={{
            direction: "end",
            children: (
              <div className="AvailableVisitsLiveMap__daysRange">
                <span>{props.maxDaysBefore}</span>
                <span>{translate(STRINGS.DAYS)}</span>
              </div>
            ),
          }}
        />
      </div>
      <div>
        <div className="AvailableVisitsLiveMap__legend">
          {renderLegend(MAP_CAREGIVER_SVG, "AvailableVisitsLiveMap__legendItem", "Home")}
          {renderLegend(MAP_AVAILABLE_VISIT_SVG, "AvailableVisitsLiveMap__legendItem", "New")}
        </div>
        <div className="AvailableVisitsLiveMap__legend">
          {renderLegend(MAP_AVAILABLE_VISIT_SVG, "AvailableVisitsLiveMap__legendItem", "Requested")}
          {renderLegend(MAP_AVAILABLE_VISIT_SVG, "AvailableVisitsLiveMap__legendItem", "Viewed")}
          {renderLegend(MAP_AVAILABLE_VISIT_SVG, "AvailableVisitsLiveMap__legendItem", "Scheduled")}
        </div>
      </div>
    </div>
  );
};
